/** YYYY-MM-DD in local timezone (for `<input type="date">` and max/min). */
export const getLocalDateInputValue = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Formats a date string or Date object to DD/MM/YYYY format with optional time
 * @param {string|Date} dateValue - The date to format
 * @param {boolean} showTime - Whether to include time in the output (defaults to false)
 * @param {string} timeFormat - Time format: '12h' for 12-hour or '24h' for 24-hour format (defaults to '24h')
 * @returns {string} Formatted date string or empty string if invalid
 */
export const formatToDisplayDate = (dateValue, showTime = false, timeFormat = '24h') => {
  if (!dateValue) return '';

  try {
    const date = new Date(dateValue);

    // Check if date is valid
    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    // Base date format
    let formattedDate = `${day}/${month}/${year}`;

    // Add time if requested
    if (showTime) {
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      let period = '';
      
      // Handle 12-hour format
      if (timeFormat === '12h') {
        period = hours >= 12 ? ' PM' : ' AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // Convert 0 to 12 for 12 AM
      }
      
      // Format hours based on selected time format
      const formattedHours = String(hours).padStart(2, '0');
      
      // Format: DD/MM/YYYY, HH:MM (with comma separator)
      formattedDate += `, ${formattedHours}:${minutes}${period}`;
    }

    return formattedDate;
  } catch (error) {
    console.error("Date formatting error:", error);
    return '';
  }
};

/**
 * Formats a date as DD/MM/YY (2-digit year). Handles YYYYMMDD strings from ledger APIs.
 * @param {string|Date|object} dateValue
 * @returns {string}
 */
export const formatToShortDisplayDate = (dateValue) => {
  if (dateValue == null || dateValue === '') return '—';

  try {
    if (typeof dateValue === 'object' && dateValue.$date) {
      return formatToShortDisplayDate(dateValue.$date);
    }

    if (typeof dateValue === 'number' && Number.isFinite(dateValue)) {
      return formatToShortDisplayDate(String(Math.trunc(dateValue)).padStart(8, '0'));
    }

    let date;
    if (typeof dateValue === 'string' && /^\d{8}$/.test(dateValue)) {
      const year = Number(dateValue.slice(0, 4));
      const month = Number(dateValue.slice(4, 6));
      const day = Number(dateValue.slice(6, 8));
      date = new Date(year, month - 1, day);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      date = new Date(dateValue);
    }

    if (isNaN(date.getTime())) {
      return typeof dateValue === 'string' ? dateValue : '—';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Short date formatting error:', error);
    return '—';
  }
};

/**
 * Formats a date-time as DD/MM/YY, HH:MM
 * @param {string|Date} dateValue
 * @returns {string}
 */
export const formatToShortDisplayDateTime = (dateValue) => {
  if (dateValue == null || dateValue === '') return '—';

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '—';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  } catch (error) {
    console.error('Short date-time formatting error:', error);
    return '—';
  }
};

/**
 * Formats a date string or Date object to ISO format for form inputs
 * @param {string|Date} dateValue - The date to format
 * @param {boolean} includeTime - Whether to include time in the output (defaults to false)
 * @returns {string} Formatted date string (YYYY-MM-DD or YYYY-MM-DDTHH:MM) or empty string if invalid
 */
export const formatToInputDate = (dateValue, includeTime = false) => {
  if (!dateValue) return '';
  
  try {
    const date = new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    // Format as YYYY-MM-DD for date inputs
    const dateString = date.toISOString().split('T')[0];
    
    // Add time if requested (for datetime-local inputs)
    if (includeTime) {
      const timeString = date.toISOString().split('T')[1].substring(0, 5);
      return `${dateString}T${timeString}`;
    }
    
    return dateString;
  } catch (error) {
    console.error("Date input formatting error:", error);
    return '';
  }
};

/**
 * Converts a date from DD/MM/YYYY or DD/MM/YYYY, HH:MM format to ISO string for API
 * @param {string} displayDate - Date in DD/MM/YYYY or DD/MM/YYYY, HH:MM format
 * @returns {string} ISO date string or empty string if invalid
 */
export const parseDisplayDate = (displayDate) => {
  if (!displayDate) return '';
  
  try {
    // Handle date with time format (DD/MM/YYYY, HH:MM)
    if (displayDate.includes(',')) {
      const [datePart, timePart] = displayDate.split(',').map(s => s.trim());
      const [day, month, year] = datePart.split('/').map(part => parseInt(part, 10));
      const [hours, minutes] = timePart.split(':').map(part => parseInt(part, 10));
      
      // Create date with time (month is 0-based in JS)
      const date = new Date(year, month - 1, day, hours, minutes);
      
      // Check if date is valid
      if (isNaN(date.getTime())) return '';
      
      return date.toISOString();
    }
    
    // Handle date-only format (DD/MM/YYYY)
    const [day, month, year] = displayDate.split('/').map(part => parseInt(part, 10));
    
    // Create date (month is 0-based in JS)
    const date = new Date(year, month - 1, day);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    return date.toISOString();
  } catch (error) {
    console.error("Date parsing error:", error);
    return '';
  }
};
